import { LayersTwo01Icon } from '@langchain/untitled-ui-icons';
import { Link } from '@mui/joy';
import { RowSelectionState } from '@tanstack/react-table';

import { FC, useState } from 'react';

import { Button, Text, Tooltip } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import { DatasetSchema, RunSchema } from '@/types/schema';
import { evaluateIfRunCanBeAddedToDataset } from '@/utils/compare-schemas';
import { appDatasetsPath, appOrganizationPath } from '@/utils/constants';
import { xCount } from '@/utils/stringUtils';

import { AddToAnnotationQueueButton } from '../AnnotationQueuePopover/AddToAnnotationQueueButton';
import { DatasetCrudPane } from '../DatasetCrudPane/DatasetCrudPane';
import { FloatingActionBar } from '../FloatingActionBar';
import { AddToDatasetButton } from './components/AddToDatasetButton';
import { BulkActionResult } from './types';

type TProps = {
  rowSelection: RowSelectionState;
  setRowSelection: (rowSelection: RowSelectionState) => void;
  selectedRuns: RunSchema[];
  mutateInfiniteRuns?: () => void;
  setBulkActionResult: (result: BulkActionResult) => void;
  runDisplayText?: string;
  handleCompareRuns?: (runIds: string[]) => void;
};

const SelectedRunsActions: FC<TProps> = ({
  rowSelection,
  setRowSelection,
  selectedRuns,
  mutateInfiniteRuns,
  setBulkActionResult,
  runDisplayText,
  handleCompareRuns,
}) => {
  const organizationId = useOrganizationId();
  const [isNewDatasetPaneOpen, setIsNewDatasetPaneOpen] = useState(false);

  const renderSelectedRunsInfo = () => {
    const numSelected = (
      <Text variant="h3">
        {`${xCount('run', Object.keys(rowSelection).length)} selected`}
      </Text>
    );
    return (
      <div className="flex w-max flex-row items-center gap-1">
        <div className="flex shrink-0 flex-col items-center justify-start">
          {numSelected}
        </div>
      </div>
    );
  };

  const canBeAddedToChatDataset =
    !selectedRuns[0] ||
    evaluateIfRunCanBeAddedToDataset(selectedRuns[0], 'chat');
  const canBeAddedToLLMDataset =
    !selectedRuns[0] ||
    evaluateIfRunCanBeAddedToDataset(selectedRuns[0], 'llm');

  const canBeAddedToKVDataset =
    !selectedRuns[0] || evaluateIfRunCanBeAddedToDataset(selectedRuns[0], 'kv');
  const handleNewDatasetCreated = (_: string, dataset: DatasetSchema) => {
    const datasetPath = `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${dataset.id}/`;
    mutateInfiniteRuns?.();
    setBulkActionResult({
      success: true,
      title: '',
      message: (
        <span>
          <span className="text-md text-white dark:text-black">{`Created new dataset with ${xCount(
            'example',
            Object.keys(rowSelection).length
          )}`}</span>
          <Link fontSize={'sm'} href={datasetPath}>
            <span className="text-md text-brand-green-200 underline">
              {dataset.name}
            </span>
          </Link>
        </span>
      ),
    });
  };

  return (
    <>
      {!isNewDatasetPaneOpen && (
        <FloatingActionBar
          label={renderSelectedRunsInfo()}
          onCancel={() => {
            setRowSelection({});
          }}
        >
          <AddToAnnotationQueueButton
            variant="toolbar"
            runIds={Object.keys(rowSelection)}
            onSuccess={() => mutateInfiniteRuns?.()}
            runDisplayText={runDisplayText}
          />
          <AddToDatasetButton
            runTypeAndInputs={selectedRuns?.[0]}
            selectedRunIds={Object.keys(rowSelection)}
            mutateInfiniteRuns={mutateInfiniteRuns}
            setBulkActionResult={setBulkActionResult}
            isNewDatasetPaneOpen={false}
            setIsNewDatasetPaneOpen={setIsNewDatasetPaneOpen}
            runDisplayText={runDisplayText}
          />
          {handleCompareRuns && (
            <Tooltip
              title={
                selectedRuns.length !== 2
                  ? 'Select two runs to compare'
                  : undefined
              }
            >
              <Button
                size="md"
                disabled={selectedRuns.length !== 2}
                leftDecorator={LayersTwo01Icon}
                onClick={() => handleCompareRuns(Object.keys(rowSelection))}
                variant="outlined"
              >
                Compare Runs
              </Button>
            </Tooltip>
          )}
        </FloatingActionBar>
      )}
      <DatasetCrudPane
        onSuccess={handleNewDatasetCreated}
        onCancel={() => setIsNewDatasetPaneOpen(false)}
        label={'New dataset'}
        open={isNewDatasetPaneOpen}
        isChatEnabled={canBeAddedToChatDataset}
        isKVEnabled={canBeAddedToKVDataset}
        isLLMEnabled={canBeAddedToLLMDataset}
        preselectedRunIds={Object.keys(rowSelection)}
        baseDatasetOnRunType={selectedRuns[0]?.run_type}
      />
    </>
  );
};

export default SelectedRunsActions;
